import { LogEntry, TraceDocument, TraceSegment } from "../models";

export function flattenTrace(
  doc: TraceDocument,
  opts: { stripSubsegments?: boolean } = { stripSubsegments: true }
): TraceSegment[] {
  if (!doc) {
    return [];
  }

  const out: TraceSegment[] = [];
  const stack: TraceSegment[] = [doc]; // TraceDocument is a TraceSegment

  while (stack.length) {
    const seg = stack.pop()!;

    if (opts.stripSubsegments) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { subsegments, ...rest } = seg as TraceSegment;
      out.push({ ...rest });
    } else {
      out.push(seg);
    }

    const children = seg.subsegments ?? [];
    for (let i = children.length - 1; i >= 0; i--) {
      stack.push(children[i]);
    }
  }

  return out;
}

export function expandTraceWithLogs(
  traceSegments: TraceSegment[],
  logs: LogEntry[]
): TraceSegment[] {
  const spanIdToLog: Record<string, LogEntry> = {};
  for (const log of logs) {
    spanIdToLog[log.span_id] = log;
  }

  return traceSegments.map((seg) => {
    const log = spanIdToLog[seg.id];
    if (log) {
      const input = log["traceloop.entity.input"];
      const output = log["traceloop.entity.output"];
      return {
        ...seg,
        metadata: {
          ...seg.metadata,
          default: {
            ...seg.metadata?.default,
            traceloop: {
              input,
              output,
            },
          },
        },
      };
    }
    return seg;
  });
}

interface Message {
  lc: number;
  type: string;
  id: string[];
  kwargs: {
    content: string | { text: string; type: string }[];
    additional_kwargs: any;
    type: string;
    id: string;
  };
}

function formatMessages(messages: Message[]): string[] {
  return messages.map((msg) => {
    const content = msg.kwargs?.content || "";
    if (!Array.isArray(content)) {
      const type = msg.kwargs?.type || "unknown";
      return `[${type}] ${content}`;
    } else {
      return content.map((c) => `[${c.type}] ${c.text}`).join("\n");
    }
  });
}

export function extractChatPrompt(traceSegments: TraceSegment[]) {
  try {
    const segments = traceSegments.filter(
      (seg) =>
        seg.metadata?.default["traceloop.entity.name"] ===
          "ChatPromptTemplate" && seg.metadata?.default["traceloop"]
    );

    return segments.map((segment) => {
      const name = `${segment.metadata?.default["traceloop.entity.name"]} (${segment.id})`;

      const input =
        segment.metadata?.default["traceloop"]["input"]["inputs"] ?? {};

      if (input["messages"]) {
        input["messages"] = formatMessages(input["messages"]);
      }

      const output = formatMessages(
        segment.metadata?.default["traceloop"]["output"]["outputs"]["kwargs"][
          "messages"
        ] ?? []
      );

      return { input, output, timestamp: segment.start_time, name };
    });
  } catch (e) {
    console.error("Error extracting chat prompt:", e);
  }
}

function processPythonSegment(segment: TraceSegment) {
  const name = `python_repl_ast (${segment.id})`;

  const input =
    (segment.metadata?.default["traceloop"]["input"]["inputs"] ?? {})[
      "query"
    ] ?? undefined;

  // const output = formatMessages(segment.metadata?.default['traceloop']['output']['output']['kwargs'] ?? []);
  const r: string[] = [];
  if (!segment.fault) {
    if (segment.metadata?.default["traceloop"]["output"]) {
      const output =
        segment.metadata?.default["traceloop"]["output"]["output"]["kwargs"] ??
        {};

      if (output["tool_call_id"]) {
        r.push(`Tool call ID: ${output["tool_call_id"]}`);
      }
      if (output["status"]) {
        r.push(`Status: ${output["status"]}`);
      }
      if (output["content"]) {
        r.push(`Result: ${output["content"]}`);
      }
      if (output["artifact"]) {
        r.push(`Artifact: ${output["artifact"]}`);
      }
    }
  } else {
    for (const key of segment.cause?.exceptions ?? []) {
      r.push(key.message);
    }
  }
  return {
    input,
    output: r.join("\n"),
    timestamp: segment.start_time,
    name,
    toolName: "python_repl_ast",
  };
}

export function extractAnalyticsCalls(traceSegments: TraceSegment[]) {
  try {
    const segments = traceSegments.filter(
      (seg) =>
        (seg.metadata?.default["traceloop.span.kind"] === "tool" &&
          seg.metadata?.default["traceloop"]) ||
        (seg.metadata?.default["traceloop.span.kind"] === "task" &&
          seg.metadata?.default["traceloop.entity.name"] === "chat")
    );
    return segments.map((segment) => {
      const toolName = segment.metadata?.default["traceloop.entity.name"];
      if (toolName === "python_repl_ast") {
        return processPythonSegment(segment);
      }

      const name = `${toolName} (${segment.id})`;

      const input =
        segment.metadata?.default["traceloop"]["input"]["inputs"] ?? {};
      if (input["state"] && input["state"]["messages"]) {
        input["state"]["messages"] = formatMessages(input["state"]["messages"]);
      }

      const output =
        (((segment.metadata?.default["traceloop"] ?? {})["output"] ?? {})[
          "output"
        ] ?? {})["kwargs"] ??
        ((segment.metadata?.default["traceloop"] ?? {})["output"] ?? {})[
          "outputs"
        ] ??
        {} ??
        {};
      return { name, input, output, timestamp: segment.start_time, toolName };
    });
  } catch (e) {
    console.error("Error extracting analytics calls:", e);
    return [];
  }
}

export function extractNotification(traceSegments: TraceSegment[]) {
  try {
    const segment = traceSegments.find(
      (seg) => seg.name === "NotificationResponseFinished"
    );
    if (segment) {
      const input = segment.metadata?.default;

      return { input, timestamp: segment.start_time };
    }
  } catch (e) {
    console.error("Error extracting notification:", e);
  }

  return {};
}
